import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { startGoogleLogin } from "@/lib/googleAuth";

export default function Login({ mode = "login" }) {
  const { login, register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [isRegister, setIsRegister] = useState(mode === "register");
  const role = "buyer"; // public sign-up is buyer-only; sellers use the Seller Center (/sell), delivery accounts are created by admin
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        const ref = new URLSearchParams(location.search).get("ref") || localStorage.getItem("xurray_ref") || undefined;
        const data = await register({ ...form, role, ref });
        const u = data?.user;
        if (u && !u.email_verified) {
          toast.success("Account created! Check your email for a verification code.");
          navigate("/verify", { state: { email: form.email } });
        } else {
          toast.success("Account created — welcome to Xurray Mart!");
          navigate("/");
        }
      } else {
        const user = await login(form.email, form.password);
        toast.success(`Welcome, ${user.name}!`);
        if (!user.email_verified) {
          navigate("/verify", { state: { email: user.email } });
        } else {
          const dest = user.role === "admin" ? "/admin" : user.role === "seller" ? "/seller" : user.role === "delivery" ? "/delivery" : "/";
          navigate(dest);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    }
    setLoading(false);
  };

  const googleLogin = async () => {
    try {
      await startGoogleLogin();
    } catch (e) {
      toast.error(e.message || "Google sign-in is unavailable right now");
    }
  };

  return (
    <Layout hideAd>
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="text-center mb-6">
          <div className="inline-block bg-white rounded-2xl p-2 mx-auto mb-3 shadow-sm"><img src="/xurray-logo-full.png" alt="Xurray Mart" className="w-20 h-20 object-contain" /></div>
          <h1 className="font-display font-bold text-2xl">{isRegister ? t("createAccount") : t("welcomeBack")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{isRegister ? t("joinMarketplace") : t("signInContinue")}</p>
        </div>

        <div className="p-6 rounded-2xl border border-border/60 bg-card">
          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <div><Label>{t("fullName")}</Label><Input data-testid="name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            )}
            <div><Label>{t("email")}</Label><Input data-testid="email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>{t("password")}</Label>
              <PasswordInput data-testid="password-input" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" />
              {!isRegister && (
                <div className="text-right mt-1.5">
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-primary hover:underline">Forgot password?</Link>
                </div>
              )}
            </div>
            <Button data-testid="submit-auth-btn" type="submit" disabled={loading} className="w-full rounded-full" size="lg">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isRegister ? t("createAccountLink") : t("signIn")}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wide">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <Button data-testid="google-login-btn" type="button" variant="outline" onClick={googleLogin} className="w-full rounded-full" size="lg">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.52 12.27c0-.82-.07-1.6-.2-2.36H12v4.47h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.74z" />
              <path fill="#34A853" d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.88-3c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.26v3.1A12 12 0 0 0 12 24z" />
              <path fill="#FBBC05" d="M5.27 14.28a7.2 7.2 0 0 1 0-4.56v-3.1H1.26a12 12 0 0 0 0 10.76l4.01-3.1z" />
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.26 6.62l4.01 3.1C6.22 6.86 8.87 4.75 12 4.75z" />
            </svg>
            Continue with Google
          </Button>
          <p className="text-center text-sm text-muted-foreground mt-4">
            {isRegister ? t("alreadyAccount") : t("newToXurray")}{" "}
            <button data-testid="toggle-auth-mode" onClick={() => setIsRegister(!isRegister)} className="text-primary font-semibold hover:underline">
              {isRegister ? t("signInLink") : t("createAccountLink")}
            </button>
          </p>
          {isRegister && (
            <p className="text-center text-sm text-muted-foreground mt-2 pt-3 border-t border-border/60">
              Want to sell on Xurray Mart?{" "}
              <Link to="/sell" data-testid="become-seller-link" className="text-primary font-semibold hover:underline">Become a Seller</Link>
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
}
